import { Link } from "@inertiajs/react"
import { Sparkles } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { creatorActivityPath } from "@/routes"

/**
 * OKS-548 — Creator-shell header entry to the Activity feed (`/activity`).
 *
 * A plain icon link (Sparkles) — distinct from the bell (which opens the
 * recent-notifications popover). The Activity feed is the full recognition
 * surface: typed rows, filters, Today/Earlier. Creator-only; lives in the
 * creator shell, so the bell + `/notifications` surface stay untouched.
 *
 * OKS-598 — `variant` picks the layout:
 *   - `"icon"` (default): the 36px icon-only trigger used inline at ≥sm.
 *   - `"row"`: a full-width, ≥44px menu row (icon + label in ONE Link) for
 *     the collapsed "…" overflow menu below sm, so the whole row — not just
 *     the icon — is the tap target. The visible label is the accessible name
 *     (icon stays `aria-hidden`).
 */
export function CreatorActivityHeaderTrigger({
  className,
  variant = "icon",
}: {
  className?: string
  variant?: "icon" | "row"
}) {
  const { t } = useTranslation()
  const label = t("activity.page.title")

  if (variant === "row") {
    return (
      <Button
        asChild
        variant="ghost"
        className={cn("h-11 w-full justify-start gap-2 px-2", className)}
      >
        <Link
          href={creatorActivityPath()}
          data-testid="creator-activity-trigger"
        >
          <Sparkles aria-hidden="true" className="size-5" />
          <span className="text-sm font-medium">{label}</span>
        </Link>
      </Button>
    )
  }

  return (
    <Button
      asChild
      variant="ghost"
      size="icon"
      className={cn("size-9", className)}
    >
      <Link
        href={creatorActivityPath()}
        data-testid="creator-activity-trigger"
        aria-label={label}
      >
        <Sparkles aria-hidden="true" className="size-5" />
      </Link>
    </Button>
  )
}
